import { useState, useEffect } from "react";
import { Link2, Unlink, Wrench, ClipboardCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

type WorkflowItem = {
  id: string;
  category: string;
  appliance_type: string;
  brand: string | null;
  title: string;
  description: string | null;
  sort_order: number;
  is_mandatory: boolean;
};

interface ItemMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceItem: WorkflowItem;
  allItems: WorkflowItem[];
  mappings: { id: string; source_item_id: string; target_item_id: string }[];
  onMappingsChange: () => void;
}

export function ItemMappingDialog({
  open,
  onOpenChange,
  sourceItem,
  allItems,
  mappings,
  onMappingsChange,
}: ItemMappingDialogProps) {
  const [saving, setSaving] = useState<string | null>(null);

  // Get linked target IDs for this source
  const linkedTargetIds = mappings
    .filter((m) => m.source_item_id === sourceItem.id)
    .map((m) => m.target_item_id);

  const troubleshootingItems = allItems.filter(
    (i) => i.category === "troubleshooting" && !i.brand
  );
  const partsItems = allItems.filter(
    (i) => i.category === "parts_replacement" && !i.brand
  );

  const toggleMapping = async (targetId: string) => {
    setSaving(targetId);
    const existing = mappings.find(
      (m) => m.source_item_id === sourceItem.id && m.target_item_id === targetId
    );
    if (existing) {
      await supabase.from("workflow_item_mappings").delete().eq("id", existing.id);
    } else {
      await supabase.from("workflow_item_mappings").insert({
        source_item_id: sourceItem.id,
        target_item_id: targetId,
      });
    }
    onMappingsChange();
    setSaving(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Link2 className="w-4 h-4 text-primary" />
            Link Steps to Error Code
          </DialogTitle>
        </DialogHeader>

        <div className="mb-4 p-3 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Error Code</p>
          <p className="text-sm font-medium text-foreground">{sourceItem.title}</p>
          {sourceItem.description && (
            <p className="text-xs text-muted-foreground mt-0.5">{sourceItem.description}</p>
          )}
        </div>

        <p className="text-xs text-muted-foreground mb-3">
          Select troubleshooting and parts replacement steps that should be activated when this error code is identified.
        </p>

        {/* Troubleshooting */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardCheck className="w-3.5 h-3.5 text-primary" />
            <p className="text-sm font-medium text-foreground">Pre-repair Troubleshooting</p>
            {linkedTargetIds.filter((id) => troubleshootingItems.some((i) => i.id === id)).length > 0 && (
              <Badge variant="secondary" className="text-[10px] h-5">
                {linkedTargetIds.filter((id) => troubleshootingItems.some((i) => i.id === id)).length} linked
              </Badge>
            )}
          </div>
          <div className="space-y-1">
            {troubleshootingItems.length === 0 ? (
              <p className="text-xs text-muted-foreground italic pl-1">No troubleshooting items available</p>
            ) : (
              troubleshootingItems.map((item) => {
                const isLinked = linkedTargetIds.includes(item.id);
                return (
                  <label
                    key={item.id}
                    className="flex items-start gap-2.5 py-1.5 px-2 rounded hover:bg-muted/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={isLinked}
                      onCheckedChange={() => toggleMapping(item.id)}
                      disabled={saving === item.id}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{item.title}</p>
                      {item.description && (
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* Parts Replacement */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-3.5 h-3.5 text-primary" />
            <p className="text-sm font-medium text-foreground">Parts Replacement Guides</p>
            {linkedTargetIds.filter((id) => partsItems.some((i) => i.id === id)).length > 0 && (
              <Badge variant="secondary" className="text-[10px] h-5">
                {linkedTargetIds.filter((id) => partsItems.some((i) => i.id === id)).length} linked
              </Badge>
            )}
          </div>
          <div className="space-y-1">
            {partsItems.length === 0 ? (
              <p className="text-xs text-muted-foreground italic pl-1">No parts replacement items available</p>
            ) : (
              partsItems.map((item) => {
                const isLinked = linkedTargetIds.includes(item.id);
                return (
                  <label
                    key={item.id}
                    className="flex items-start gap-2.5 py-1.5 px-2 rounded hover:bg-muted/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={isLinked}
                      onCheckedChange={() => toggleMapping(item.id)}
                      disabled={saving === item.id}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{item.title}</p>
                      {item.description && (
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
